import React from 'react';

import {NucleotideType} from 'biojs/Util';
import DataConnector from 'connect/DataConnector';
import {AllPolymerLayouts} from 'constants/MapsConstants';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {
  ModifiedNucleotidesQuery,
  ModifiedNucleotidesQueryVariables,
} from 'molbio/monomers/__generated/ModifiedNucleotidesQuery';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {getDisplayModsForSlice} from 'molbio/monomers/ModificationVisualizationUtil';
import {DEFAULT_MODIFIED_SUGAR_COLOR, MONOMER_QUERY} from 'molbio/monomers/MonomerConstants';
import {MODIFIED_NUCLEOTIDES_QUERY} from 'molbio/monomers/NucleotidesTab';
import getTestProps from 'test/lib/cypress/getTestProps';

import {ModifiedNucleotideGraphQL} from '../../../../graphql-fragments/modbio/__generated/ModifiedNucleotideGraphQL';
import ComponentWrapper from './ComponentWrapper';
import {widthBetweenLetters} from './LinearMapUtil';

type ModifiedNucleotide = NonNullable<
  ModifiedNucleotidesQuery['componentSequence']
>['modifiedNucleotides'][number];

export interface ModifiedNucleotidesAxisOverlayProps {
  xFn: (index: number) => number;
  sliceStart: number;
  sliceEnd: number;
  sequenceId: string;
  layout: AllPolymerLayouts['nucleotidesAxisOverlay'];
  nucleotideType: NucleotideType;
  bases: string;
}

interface EnhancedProps {
  hoveredMonomerIds: Array<string | null>;
  hoveredNucleotideIdx: number | null;
  selectedPositions: Set<number>;
}

class ModifiedNucleotidesAxisOverlayConnector extends DataConnector<
  ModifiedNucleotidesAxisOverlayProps,
  EnhancedProps
> {
  enhanceProps(): EnhancedProps {
    const state = this.reduxState().ui.molbio.nucleotidesUI[this.props.sequenceId];
    return {
      hoveredMonomerIds: state?.hoveredMonomerIds ?? [],
      hoveredNucleotideIdx: state?.hoveredIdx,
      selectedPositions: state?.selectedPositions ?? new Set(),
    };
  }
}

interface PresentationalProps {
  monomers: MonomerQuery['monomers'];
  modifiedNucleotides: Array<ModifiedNucleotideAttributes>;
}

export function PresentationalNucleotidesAxisOverlay({
  hoveredMonomerIds,
  hoveredNucleotideIdx,
  xFn,
  sliceStart,
  sliceEnd,
  layout,
  bases,
  selectedPositions,
  modifiedNucleotides,
  monomers,
  nucleotideType,
}: Omit<
  ModifiedNucleotidesAxisOverlayProps & EnhancedProps & PresentationalProps,
  'sequenceId'
>): JSX.Element {
  const letterCenterX = (i: number): number =>
    (i - sliceStart) * widthBetweenLetters(xFn, sliceStart) + 0.5 * widthBetweenLetters(xFn, sliceStart);

  function shouldDisplayAxisDot(mod: ModifiedNucleotide | null, position: number): boolean {
    if (hoveredNucleotideIdx === position) {
      return true;
    }
    if (!hoveredMonomerIds.length || !mod) {
      return false;
    }
    return hoveredMonomerIds.some((monomerId) =>
      [mod.baseMonomerId, mod.sugarMonomerId, mod.phosphateMonomerId].includes(monomerId)
    );
  }
  function shouldDisplayAxisUnderline(position: number): boolean {
    return selectedPositions.has(position);
  }
  const displayMods = getDisplayModsForSlice(
    monomers,
    nucleotideType,
    bases,
    modifiedNucleotides,
    sliceStart
  );

  const nucleotidesData = [];
  for (let position = sliceStart; position < sliceEnd; position++) {
    const modifiedNucleotide = displayMods.find((mn) => mn.position === position) || null;
    nucleotidesData.push([modifiedNucleotide, position]);
  }
  return (
    <svg>
      <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
        <g {...getTestProps({component: 'NucleotidesAxisOverlay', element: 'root'})}>
          {nucleotidesData.map(([mod, position]: [ModifiedNucleotideGraphQL | null, number]) => (
            <g key={position}>
              {shouldDisplayAxisUnderline(position) && (
                <rect
                  x={xFn(position)}
                  y={-2}
                  width={widthBetweenLetters(xFn, sliceStart)}
                  height="4px"
                  fill={DEFAULT_MODIFIED_SUGAR_COLOR}
                  shapeRendering="geometricPrecision"
                  {...getTestProps({
                    component: 'NucleotidesAxisOverlay',
                    element: 'underline',
                    key: String(position),
                  })}
                  data-amp-safe={false}
                />
              )}
              {shouldDisplayAxisDot(mod, position) && !shouldDisplayAxisUnderline(position) && (
                <circle
                  cx={letterCenterX(position)}
                  cy={0}
                  r={3}
                  fill={DEFAULT_MODIFIED_SUGAR_COLOR}
                  shapeRendering="geometricPrecision"
                  {...getTestProps({
                    component: 'NucleotidesAxisOverlay',
                    element: 'dot',
                    key: String(position),
                  })}
                  data-amp-safe={false}
                />
              )}
            </g>
          ))}
        </g>
      </ComponentWrapper>
    </svg>
  );
}

function NucleotidesAxisOverlay(props: ModifiedNucleotidesAxisOverlayProps & EnhancedProps): JSX.Element {
  return (
    <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}>
      {(monomerData) => (
        <BenchlingQuery<ModifiedNucleotidesQuery, ModifiedNucleotidesQueryVariables>
          query={MODIFIED_NUCLEOTIDES_QUERY}
          renderLoading={() => null}
          variables={{sequenceId: props.sequenceId}}
          fetchPolicy="cache-only"
        >
          {(data) => (
            <PresentationalNucleotidesAxisOverlay
              monomers={monomerData.monomers}
              modifiedNucleotides={data.componentSequence!.modifiedNucleotides}
              {...props}
            />
          )}
        </BenchlingQuery>
      )}
    </BenchlingQuery>
  );
}

export default ModifiedNucleotidesAxisOverlayConnector.connect(NucleotidesAxisOverlay);
